import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { School, Mail, Check, AlertCircle, ArrowLeft, Loader } from 'lucide-react';
import toast from 'react-hot-toast';

const WaitlistPage = () => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [schools, setSchools] = useState([]);
  const [formData, setFormData] = useState({
    email: '',
    schoolId: ''
  });
  const [success, setSuccess] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  
  // Extract email from query params if coming from redirect
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const emailParam = params.get('email');
    if (emailParam) {
      setFormData(prev => ({ ...prev, email: emailParam }));
    }
  }, [location]);
  
  // Fetch paused schools for the waitlist
  useEffect(() => {
    const fetchPausedSchools = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/waitlist/schools`);
        
        if (!response.ok) {
          throw new Error('Failed to fetch schools');
        }
        
        const data = await response.json();
        setSchools(data);
        
        // Pre-select the school if only one is available
        if (data.length === 1) {
          setFormData(prev => ({ ...prev, schoolId: data[0].id }));
        }
      } catch (error) {
        console.error('Error fetching schools:', error);
        toast.error('Failed to load available schools');
      } finally {
        setLoading(false);
      }
    };
    
    fetchPausedSchools();
  }, []);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };
  
  const validateForm = () => {
    if (!formData.email || !formData.schoolId) {
      toast.error('Email and school selection are required');
      return false;
    }
    
    // Find the selected school
    const selectedSchool = schools.find(school => school.id === formData.schoolId);
    if (!selectedSchool) {
      toast.error('Please select a valid school');
      return false;
    }
    
    // Validate email domain matches the school domain
    const emailDomain = formData.email.split('@')[1];
    if (!emailDomain || emailDomain !== selectedSchool.domain) {
      toast.error(`Please use an email with the domain ${selectedSchool.domain}`);
      return false;
    }
    
    return true;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setSubmitting(true);
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/waitlist/join`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to join waitlist');
      }
      
      setSuccess(true);
    } catch (error) {
      console.error('Error joining waitlist:', error);
      toast.error(error.message || 'Failed to join the waitlist');
    } finally {
      setSubmitting(false);
    }
  };
  
  // Success screen after joining waitlist
  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8 text-center">
          <div>
            <div className="flex justify-center">
              <div className="text-4xl font-bold text-red-500">EDX</div>
              <div className="text-4xl font-bold text-black dark:text-white">Red</div>
            </div>
            
            <div className="mx-auto mt-6 flex items-center justify-center h-12 w-12 rounded-full bg-green-100 dark:bg-green-900">
              <Check className="h-6 w-6 text-green-600 dark:text-green-300" />
            </div>
            <h2 className="mt-4 text-center text-3xl font-extrabold text-gray-900 dark:text-white">
              You're on the Waitlist!
            </h2>
          </div>
          
          <div className="mt-2 p-4 bg-green-50 dark:bg-green-900 rounded-md">
            <p className="text-sm text-green-800 dark:text-green-200">
              We've added <strong>{formData.email}</strong> to the waitlist. 
              We'll notify you as soon as registrations open for your school.
            </p>
          </div>
          
          <div className="mt-6">
            <Link
              to="/"
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              Return to Homepage
            </Link>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="flex justify-center">
            <div className="text-4xl font-bold text-red-500">EDX</div>
            <div className="text-4xl font-bold text-black dark:text-white">Red</div>
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900 dark:text-white">
            Join the Waitlist
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
            We'll notify you when EDX Red becomes available at your school
          </p>
        </div>
        
        {loading ? (
          <div className="flex justify-center items-center py-10">
            <Loader className="h-10 w-10 text-red-500 animate-spin" />
          </div>
        ) : schools.length === 0 ? (
          <div className="text-center py-8">
            <School className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No Schools Available</h3>
            <p className="text-gray-500 dark:text-gray-400 mb-4">
              There are currently no schools with active waitlists.
            </p>
            <Link 
              to="/"
              className="inline-flex items-center text-sm font-medium text-red-600 hover:text-red-500"
            >
              <ArrowLeft size={16} className="mr-1" /> Return to Homepage
            </Link>
          </div>
        ) : (
          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            <div className="bg-yellow-50 dark:bg-yellow-900 border border-yellow-200 dark:border-yellow-800 rounded-md p-4 mb-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <AlertCircle className="h-5 w-5 text-yellow-400" aria-hidden="true" />
                </div>
                <div className="ml-3">
                  <p className="text-sm text-yellow-700 dark:text-yellow-200">
                    You must use a valid <strong>.edu</strong> email address that matches your selected school.
                  </p>
                </div>
              </div>
            </div>
          
            <div className="rounded-md shadow-sm -space-y-px">
              <div>
                <label htmlFor="schoolId" className="sr-only">School</label>
                <select
                  id="schoolId"
                  name="schoolId"
                  required
                  value={formData.schoolId}
                  onChange={handleChange}
                  className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-700 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white dark:bg-gray-800 rounded-t-md focus:outline-none focus:ring-red-500 focus:border-red-500 focus:z-10 sm:text-sm"
                >
                  <option value="">Select your school</option>
                  {schools.map(school => (
                    <option key={school.id} value={school.id}>
                      {school.name} ({school.domain})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="email" className="sr-only">Email address</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-700 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white dark:bg-gray-800 rounded-b-md focus:outline-none focus:ring-red-500 focus:border-red-500 focus:z-10 sm:text-sm"
                  placeholder="Email address (.edu required)"
                />
              </div>
            </div>
            
            <div>
              <button
                type="submit"
                disabled={submitting}
                className={`group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 ${
                  submitting ? 'opacity-70 cursor-not-allowed' : ''
                }`}
              >
                <span className="absolute left-0 inset-y-0 flex items-center pl-3">
                  {submitting ? (
                    <Loader className="h-5 w-5 text-red-500 group-hover:text-red-400 animate-spin" />
                  ) : (
                    <Mail className="h-5 w-5 text-red-500 group-hover:text-red-400" />
                  )}
                </span>
                {submitting ? 'Submitting...' : 'Join Waitlist'}
              </button>
            </div>
            
            <div className="text-center mt-4">
              <Link 
                to="/" 
                className="inline-flex items-center text-sm font-medium text-red-600 hover:text-red-500"
              >
                <ArrowLeft size={16} className="mr-1" /> Return to Homepage
              </Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default WaitlistPage;